import React, { useState, useContext } from 'react';
import PresetDndContext from '../common/DnD/PresetDndContext';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import SortableItem from '../common/DnD/SortableItem';
import { handleSortUpdate } from '../common/DnD/sortUpdater';
import Icon from '../common/Icon';
import Modal from '../common/Modal';
import AddUserFilterField from './AddUserFilterField';
import { notify } from '../../helpers/notifications';
import {
  createUserFilterField,
  destroyUserFilterField,
} from './constants/handleUserFilterFields';
import { adjustSortablesAfterDelete, dragSort } from '../../helpers/sorters';
import { FormContext, UserContext } from '../../hooks/Contexts';

const CustomFilterFields = ({ userFilterFields, setUserFilterFields }) => {
  const { form, fieldsets } = useContext(FormContext);
  const user = useContext(UserContext);
  const [addingUserFilterField, setAddingUserFilterField] = useState(false);
  const uffIds = userFilterFields.map((uff) => uff.field_id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let sort = userFilterFields.map((uff) => uff.id).indexOf(over.id);
      let backup = [...userFilterFields];
      setUserFilterFields((uffs) => dragSort(uffs, active, over));
      let sorted = await handleSortUpdate(
        'user_filter_fields',
        active.id,
        sort
      );
      if (!sorted.ok) setUserFilterFields(backup);
    }
  };

  const onCreateSuccess = (filterField) => {
    setUserFilterFields((uffs) => {
      return [...uffs, filterField];
    });
    setAddingUserFilterField(false);
  };

  const handleModal = () => {
    if (userFilterFields.length === 8) {
      return notify.alertWarning(
        'Notice',
        'Maxmimum number of filter fields reached.'
      );
    }
    setAddingUserFilterField(true);
  };

  const handleAddUserFilterField = async (fieldID, e) => {
    if (!fieldID) return;
    let uff = {
      form_id: form.id,
      field_id: fieldID,
      sort: userFilterFields.length,
    };
    // type = 'user' or 'external_user'
    uff[user.type + '_id'] = user.id;

    let created = await createUserFilterField(uff);
    if (created.ok) {
      onCreateSuccess(created.data.user_filter_field);
    }
  };

  const handleDestroy = async (uff) => {
    await destroyUserFilterField(uff, (response) => {
      if (response.ok)
        setUserFilterFields(
          adjustSortablesAfterDelete(userFilterFields, uff.id)
        );
    });
  };

  // Non-draggable, non-editable columns (ID, Submitted)
  const LockedField = ({ columnName }) => (
    <li className='ui-sortable-handle locked'>
      <div className='cargo'>
        <div className='name' title={columnName}>
          {columnName}
        </div>
      </div>
    </li>
  );

  // Draggable, editable filter fields
  const UserFilterField = ({ uff }) => (
    <div className='cargo'>
      <div className='name'>{uff.field.name}</div>
      <span>
        <Icon
          className='destroy'
          onClick={() => {
            handleDestroy(uff);
          }}
          icon='xmark'
        />
      </span>
    </div>
  );

  return (
    <>
      <section className='filter-fields'>
        <h2>
          Customized Filters ({userFilterFields.length}/8)
          <Icon
            role='button'
            title='Add filterable field'
            onClick={() => handleModal()}
            icon='plus fa-border'
          />
        </h2>

        <Modal
          show={addingUserFilterField}
          onClose={() => setAddingUserFilterField(false)}
          title='filter-field-creation-modal'
        >
          <AddUserFilterField
            fieldsets={fieldsets}
            handleAddUserFilterField={handleAddUserFilterField}
            uffIds={uffIds}
            filtersFull={userFilterFields.length === 8}
          />
        </Modal>

        <div className='details'>
          <PresetDndContext handleDragEnd={handleDragEnd}>
            <ol className='orderable ui-sortable'>
              <SortableContext
                strategy={horizontalListSortingStrategy}
                items={userFilterFields}
              >
                <LockedField columnName={'ID'} />
                {userFilterFields ? (
                  <>
                    {userFilterFields.slice(0, 4).map((uff) => {
                      return (
                        <SortableItem
                          key={uff.id}
                          id={uff.id}
                          className='ui-sortable-handle'
                          title={uff.field.name}
                        >
                          <UserFilterField uff={uff} />
                        </SortableItem>
                      );
                    })}
                    {userFilterFields.length > 4 && (
                      <>
                        <li
                          className='divider'
                          title={'<- Always visible | Expanded filters ->'}
                        >
                          <div className='cargo'>
                            <div className='name'>
                              <i className='fa-solid fa-left-right' />
                            </div>
                          </div>
                        </li>
                        {userFilterFields.slice(4).map((uff) => {
                          return (
                            <SortableItem
                              key={uff.id}
                              id={uff.id}
                              className='ui-sortable-handle'
                              title={uff.field.name}
                            >
                              <UserFilterField uff={uff} />
                            </SortableItem>
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  <li className='empty'>
                    Doesn't look like you've added any filters...
                  </li>
                )}
                <LockedField columnName={'Submitted'} />
              </SortableContext>
            </ol>
          </PresetDndContext>
        </div>
      </section>
    </>
  );
};

export default CustomFilterFields;
